import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { PaperClipIcon, DocumentTextIcon, CheckBadgeIcon, StarIcon } from '@heroicons/react/24/outline';
import { inventoryAPI } from '../../../services/inventoryApi';
import { openFileInNewTab } from '../../../services/fileApi';

const money = (n) => `Rs ${(Math.round((Number(n) || 0) * 100) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

const STATUS = {
    received: { label: 'Received', cls: 'bg-gray-100 text-gray-700' },
    selected: { label: 'Selected', cls: 'bg-green-100 text-green-800' },
    not_selected: { label: 'Not selected', cls: 'bg-gray-100 text-gray-500' },
    cancelled: { label: 'Cancelled', cls: 'bg-red-100 text-red-700' },
};

/**
 * Read-only summary of a purchase request's supplier quotations (shown in the PR view once approved).
 * Quotations are entered, analysed and turned into the purchase order on Procurement → Supplier Quotations.
 */
const PurchaseRequestQuotations = ({ purchaseRequest }) => {
    const [quotations, setQuotations] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        if (!purchaseRequest?._id) return;
        setLoading(true);
        try {
            const res = await inventoryAPI.getSupplierQuotations({ purchaseRequest: purchaseRequest._id, limit: 200, sortBy: 'createdAt', sortOrder: 'asc' });
            setQuotations(res.data || []);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to load the quotations');
        } finally { setLoading(false); }
    }, [purchaseRequest?._id]);

    useEffect(() => { load(); }, [load]);

    if (!['approved', 'converted_to_po'].includes(purchaseRequest?.status)) return null;

    const active = quotations.filter(q => q.status !== 'cancelled');
    const lowest = active.length ? Math.min(...active.map(q => q.totals?.grandTotal ?? Infinity)) : null;
    const pageLink = `/inventory/supplier-quotations?pr=${purchaseRequest._id}`;

    return (
        <div data-testid="pr-quotations">
            <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-gray-900 flex items-center">
                    <DocumentTextIcon className="w-4 h-4 mr-1" />
                    Supplier Quotations {quotations.length ? `(${quotations.length})` : ''}
                </h4>
                <Link to={pageLink} className="text-xs font-medium text-indigo-600 hover:underline">
                    Manage in Supplier Quotations &rarr;
                </Link>
            </div>

            {loading ? (
                <p className="text-sm text-gray-400 bg-gray-50 rounded-lg p-3">Loading quotations…</p>
            ) : quotations.length === 0 ? (
                <p className="text-sm text-gray-500 bg-amber-50 border border-amber-100 rounded-lg p-3">
                    No quotations yet.
                    {purchaseRequest.status === 'approved' && ' Enter the suppliers\' quotations in Procurement → Supplier Quotations — one must be selected before this request can become a purchase order.'}
                </p>
            ) : (
                <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quotation</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Delivery</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Valid Until</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {quotations.map(q => {
                                const isLowest = q.status !== 'cancelled' && lowest != null && q.totals?.grandTotal <= lowest + 0.005;
                                const st = STATUS[q.status] || STATUS.received;
                                return (
                                    <tr key={q._id} className={q.status === 'selected' ? 'bg-green-50/60' : ''}>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <div className="font-medium text-gray-900">{q.quotationNumber}</div>
                                            {q.supplierReference && <div className="text-xs text-gray-500">Ref: {q.supplierReference}</div>}
                                        </td>
                                        <td className="px-3 py-2 text-gray-900">
                                            {q.supplier?.name || '—'}
                                            {q.attachments?.map((a, i) => (
                                                <button key={a.fileId || i} type="button" onClick={() => openFileInNewTab(a.fileId).catch(() => toast.error('Could not open the document'))}
                                                    className="flex items-center text-xs text-indigo-600 hover:underline">
                                                    <PaperClipIcon className="w-3 h-3 mr-0.5" />{a.name}
                                                </button>
                                            ))}
                                        </td>
                                        <td className="px-3 py-2 text-right whitespace-nowrap">
                                            <span className="font-semibold text-gray-900">{money(q.totals?.grandTotal)}</span>
                                            {isLowest && quotations.length > 1 && (
                                                <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800" title="Lowest total">
                                                    <StarIcon className="w-3 h-3 mr-0.5" />Lowest
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{q.deliveryDays != null ? `${q.deliveryDays} days` : '—'}</td>
                                        <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{fmtDate(q.validUntil)}</td>
                                        <td className="px-3 py-2">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>
                                                {q.status === 'selected' && <CheckBadgeIcon className="w-3.5 h-3.5 mr-0.5" />}{st.label}
                                            </span>
                                            {q.status === 'selected' && q.selectionReason && (
                                                <div className="text-xs text-gray-500 mt-0.5 max-w-[220px]">Reason: {q.selectionReason}</div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default PurchaseRequestQuotations;
